import React, {useMemo} from 'react';
import {Input, message, Modal, Select, Space, Typography} from 'antd';
import {useMutation, useQuery} from '@apollo/client';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
  GET_QC_POD_EXEMPT_REPORTS,
  GET_QC_POD_EXEMPT_REVIEW_STATS,
  QcPodExemptReportsResponse,
  QcPodExemptReviewStatsResponse,
} from '../../../graphql/podExempt/queries';
import {
  QC_OFFLINE_POD_EXEMPT_REPORT,
  QcOfflinePodExemptReportResponse,
} from '../../../graphql/podExempt/mutations';
import {
  PodExemptReportFiltersInput,
  PodExemptReportType,
  PodExemptStatus,
} from '../../../graphql/podExempt/types';
import {translateGraphQLError} from '../../../utils/graphqlUtils';
import PodExemptReportTable from '../../../components/podExempt/PodExemptReportTable';
import PodExemptReviewQueueStats from '../../../components/podExempt/PodExemptReviewQueueStats';
import TablePaginationBar from '../../../components/TablePaginationBar';
import {usePodExemptDict} from '../../../hooks/usePodExemptDict';

/** 审核后台：门牌免拍终审队列（点「审核」进入审核页；全量数据域）。 */
const QcPodExemptListPage: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  // 原型队列无状态筛选：默认展示全部状态（各状态在状态列区分）
  /**
   * 筛选与分页存 URL query，而不是组件 state。
   *
   * 从列表点进审核页会卸载本组件，state 随之丢失 —— 审核完返回列表，原先选好的
   * 「待初审」和页码全被重置，得重新选一遍。放进 URL 后：返回列表带着 query 回来即可复原，
   * 刷新不丢，筛好的列表链接也能直接发给别人。
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = (searchParams.get('status') ?? '') as PodExemptStatus | '';
  const searchTextParam = searchParams.get('q') ?? '';
  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('size')) || 10;
  const filters: PodExemptReportFiltersInput = useMemo(
    () => ({
      statuses: statusParam ? [statusParam] : null,
      searchText: searchTextParam || null,
    }),
    [statusParam, searchTextParam],
  );
  /** 改筛选/翻页统一走这里；传 null 即删除该参数（回到默认值），replace 避免每次筛选都堆一条历史 */
  const patchParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setSearchParams(next, {replace: true});
  };

  const {data, loading, refetch} = useQuery<QcPodExemptReportsResponse>(GET_QC_POD_EXEMPT_REPORTS, {
    variables: {filters, pageRequest: {page, size: pageSize}},
    fetchPolicy: 'cache-and-network',
  });
  const {data: statsData, refetch: refetchStats} = useQuery<QcPodExemptReviewStatsResponse>(
    GET_QC_POD_EXEMPT_REVIEW_STATS,
    {fetchPolicy: 'cache-and-network'},
  );

  const [qcOfflineReport] = useMutation<QcOfflinePodExemptReportResponse>(QC_OFFLINE_POD_EXEMPT_REPORT);

  const {items: reasonDictAll} = usePodExemptDict('REASON');
  const {items: addressTypeDictAll} = usePodExemptDict('ADDRESS_TYPE');
  const dictNameOf = useMemo(() => {
    const map = new Map<string, string>();
    [...reasonDictAll, ...addressTypeDictAll].forEach((item) => map.set(item.code, item.name));
    return (code: string) => map.get(code) ?? code;
  }, [reasonDictAll, addressTypeDictAll]);

  const pageData = data?.qcPodExemptReports;

  const openReview = (reportId: string) =>
    navigate(`/qualitycontrol/pod-exempt/review/${reportId}?${searchParams}`);

  /** 中台人工下线：仅已生效单可下线；MANUAL 语义（DSP 仍可重报），与抽查下线的永久拦截区分 */
  const handleOffline = (record: PodExemptReportType) => {
    Modal.confirm({
      title: t('podExempt.offline.confirmTitle'),
      content: t('podExempt.offline.confirmContent', {display: record.displayAddress ?? record.reportNo}),
      okButtonProps: {danger: true},
      onOk: async () => {
        try {
          await qcOfflineReport({variables: {reportId: record.id}});
          message.success(t('podExempt.offline.success'));
          refetch();
          refetchStats();
        } catch (e) {
          message.error(translateGraphQLError(e, 'podExempt.offline.failed'));
        }
      },
    });
  };

  return (
    <div className="p-4">
      <Typography.Title level={4}>{t('podExempt.menu.qc')}</Typography.Title>
      {/* 原型布局：统计卡行右侧放单框搜索（底部对齐，无状态筛选） */}
      {/* flex-wrap 必需：统计卡约 804px + 筛选区 456px（w-32 + w-80）+ 间距 ≈ 1308px，再加左侧菜单约 340px，
          不换行需要约 1650px 视口才不溢出 —— 窄屏下搜索框与「筛选」按钮会被推出可视区 */}
      <div className="flex items-end justify-between gap-4 mb-3 flex-wrap">
        <PodExemptReviewQueueStats
          stats={statsData?.qcPodExemptReviewStats ?? null}
          overdueLabel={t('podExempt.review.statOver24h')}
          passRateLabel={t('podExempt.review.statWeeklyPassRateFinal')}
          pendingLabel={t('podExempt.status.pendingFinal')}
        />
        <div className="flex gap-2 flex-none mb-1">
          <Select<PodExemptStatus | ''>
            className="w-32"
            value={statusParam}
            onChange={(value) => {
              patchParams({status: value || null, page: null});
              refetchStats();
            }}
            options={[
              {value: '', label: t('podExempt.filter.allStatus')},
              {value: 'PENDING_FIRST', label: t('podExempt.status.pendingFirst')},
              {value: 'PENDING_FINAL', label: t('podExempt.status.pendingFinal')},
              {value: 'EFFECTIVE', label: t('podExempt.status.effective')},
              {value: 'REJECTED', label: t('podExempt.status.rejected')},
              {value: 'CLOSED', label: t('podExempt.status.closed')},
              {value: 'OFFLINE', label: t('podExempt.status.offline')},
            ]}
          />
          <Input.Search
            className="w-80"
            allowClear
            enterButton={t('podExempt.filter.search')}
            placeholder={t('podExempt.review.searchPlaceholder') ?? ''}
            onSearch={(value) => {
              patchParams({q: value.trim() || null, page: null});
              // 统计卡与列表一体刷新（统计不带筛选参数，需手动触发）
              refetchStats();
            }}
          />
        </div>
      </div>
      <PodExemptReportTable
        variant="review"
        data={pageData?.content ?? []}
        loading={loading}
        dictNameOf={dictNameOf}
        onRowClick={(record) => openReview(record.id)}
        renderActions={(record) => (
          <Space size="middle">
            <Typography.Link onClick={() => openReview(record.id)}>
              {record.status === 'PENDING_FINAL'
                ? t('podExempt.review.reviewAction')
                : t('podExempt.common.detail')}
            </Typography.Link>
            {record.status === 'EFFECTIVE' && (
              <Typography.Link type="danger" onClick={() => handleOffline(record)}>
                {t('podExempt.offline.action')}
              </Typography.Link>
            )}
          </Space>
        )}
      />
      <TablePaginationBar
        total={Number(pageData?.totalElements ?? 0)}
        current={page}
        pageSize={pageSize}
        onChange={(nextPage, nextSize) => {
          patchParams({
            page: nextPage > 1 ? String(nextPage) : null,
            size: nextSize !== 10 ? String(nextSize) : null,
          });
          refetchStats();
        }}
      />
    </div>
  );
};

export default QcPodExemptListPage;
